import { useEffect, useRef } from 'react'
import './ui.css'
import { formatMoney, formatQty } from '../../lib/money.js'

// The component inventory (Build Book §11a).
// A feature folder may not define a formatter, a modal or a colour.

/**
 * Money. Decimal string in, en-IN grouping out, two decimals always,
 * mono tabular-nums. Never a Number in, because JSON numbers are doubles.
 */
export function Money({ value, tone = 'none', prefix = true }) {
  // No value is an em dash on its own. "₹—" reads as a currency amount that
  // failed to load; "—" reads as "nothing here", which is what it means.
  const absent = value === null || value === undefined || value === ''
  return (
    <span className={`ui-money ui-tone-${absent ? 'none' : tone}`}>
      {prefix && !absent ? '₹' : ''}
      {formatMoney(value)}
    </span>
  )
}

/**
 * A checkmark, drawn rather than typed. The Unicode glyph (✓) sits inside a
 * monospace character cell built for a digit's width, not its own — every
 * font renders it at a different size and offset within that cell, so next
 * to a plain "2" in the same circle it reads as not-quite-centred instead of
 * done. A stroked path has no such cell to disagree with; it is exactly what
 * `viewBox` says it is, in this font or the next one.
 */
export function CheckIcon({ size = 12 }) {
  return (
    <svg
      className="ui-check-icon"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 8.5l3.2 3.2L13 4.8" />
    </svg>
  )
}

/** Quantities. Three decimals, never rounded, with their unit. */
export function Qty({ value, unit }) {
  return (
    <span className="ui-qty">
      {formatQty(value)}
      {unit ? <span className="ui-qty-unit"> {unit}</span> : null}
    </span>
  )
}

/**
 * Two-line cell: primary value above, muted context below.
 * Twice the information, same row height — which is what makes twenty lines
 * fit on one screen.
 */
export function Stack({ main, sub }) {
  return (
    <span className="ui-stack">
      <span className="ui-stack-main">{main}</span>
      {sub ? <span className="ui-stack-sub">{sub}</span> : null}
    </span>
  )
}

/**
 * One derivation, shared with the server's vocab.payment_state().
 * Shape and colour read at a glance; a word does not.
 */
export function PaymentPill({ received, total, dueDate, today }) {
  const state = paymentState(received, total, dueDate, today)
  const label = {
    paid: 'Paid',
    partial: 'Partial',
    unpaid: 'Unpaid',
    overdue: overdueLabel(dueDate, today),
  }[state]
  return <span className={`ui-pill ui-pill-${state}`}>{label}</span>
}

export function paymentState(received, total, dueDate, today) {
  const r = Number(received ?? 0)
  const t = Number(total ?? 0)
  if (r >= t) return 'paid'
  if (dueDate && today && dueDate < today) return 'overdue'
  if (r > 0) return 'partial'
  return 'unpaid'
}

function overdueLabel(dueDate, today) {
  if (!dueDate || !today) return 'Overdue'
  const days = Math.floor(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${dueDate}T00:00:00Z`)) / 86400000,
  )
  return days > 0 ? `Overdue ${days}d` : 'Overdue'
}

/**
 * The primary button has no shadow, weight 400, 4px radius, and asymmetric
 * vertical padding that optically centres the label (§09).
 */
export function Button({
  variant = 'secondary',
  type = 'button',
  loading = false,
  children,
  ...rest
}) {
  return (
    <button
      type={type}
      className={`ui-btn ui-btn-${variant}`}
      // A loading button that stays clickable is how a double-tap becomes two
      // invoices even with an idempotency key in place.
      disabled={loading || rest.disabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="ui-spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  )
}

/**
 * Label 13px/600 above the field. Help text 12.5px muted below. The error
 * state swaps the border and prints fields[key] from the envelope underneath.
 */
export function Field({ label, htmlFor, help, error, children, required }) {
  const describedBy = [error ? `${htmlFor}-err` : null, help ? `${htmlFor}-help` : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`ui-field${error ? ' ui-field-error' : ''}`}>
      <label className="ui-label" htmlFor={htmlFor}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {typeof children === 'function' ? children({ describedBy }) : children}
      {help && !error ? (
        <p className="ui-help" id={`${htmlFor}-help`}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p className="ui-error" id={`${htmlFor}-err`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export function TextInput({ id, describedBy, ...rest }) {
  return <input id={id} className="ui-input" aria-describedby={describedBy || undefined} {...rest} />
}

/** A password box that is never controlled. React copies a controlled input's
 *  value into the HTML `value` attribute on every keystroke, password boxes
 *  included, so Inspect showed `value="the password"` in plain text. `value`
 *  and `defaultValue` are dropped on purpose: read the password from the form
 *  on submit (`form.elements[name].value`) and clear it there. */
export function PasswordInput({ id, describedBy, value: _value, defaultValue: _default, ...rest }) {
  return (
    <input
      id={id}
      type="password"
      className="ui-input"
      aria-describedby={describedBy || undefined}
      {...rest}
    />
  )
}

/** Every quantity and amount field is inputmode="decimal" and selects on focus. */
export function DecimalInput({ id, describedBy, ...rest }) {
  return (
    <input
      id={id}
      className="ui-input ui-input-numeric"
      inputMode="decimal"
      autoComplete="off"
      onFocus={(e) => e.target.select()}
      aria-describedby={describedBy || undefined}
      {...rest}
    />
  )
}

/** Empty states are onboarding. Each one names the next action. */
export function EmptyState({ title, action }) {
  return (
    <div className="ui-empty">
      <p className="ui-empty-title">{title}</p>
      {action ? <div className="ui-empty-action">{action}</div> : null}
    </div>
  )
}

/**
 * A form that has to interrupt whatever screen needed it — most often "this
 * supplier/product/customer does not exist yet" in the middle of a document
 * that cannot be posted without one. Centred rather than a drawer: the thing
 * behind it is usually a form of its own, and a drawer would sit on top of the
 * exact field the person is about to come back to.
 *
 * No portal. `position: fixed` escapes overflow and stacking on every screen
 * that opens one today, and `<ToastProvider>` gets away with the same thing —
 * a portal earns its keep only once something wraps a fixed ancestor in a
 * transform, which nothing here does.
 *
 * Which is also why it is a boundary for events. The quick-add forms open from
 * inside another form — the purchase, the bill — and React bubbles a submit
 * along the component tree, portal or not. Without the stop below, "Add tile"
 * also submitted the purchase behind it: a half-filled purchase lit up with
 * errors, and a complete one was posted without the new tile. Enter is stopped
 * for the same reason: the purchase form swallows Enter in its inputs, which
 * left the quick-add form unsubmittable from the keyboard.
 */
export function Modal({ open, onClose, title, children, wide = false }) {
  const dialogRef = useRef(null)
  const lastFocused = useRef(null)
  // Read through a ref so the effect below depends on `open` alone. Callers
  // pass a fresh arrow on every render, and an effect keyed on it re-ran on
  // every re-render behind the modal: focus went to the opener, then to the
  // dialog, and the field being typed in lost it.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Captured while rendering, before an autoFocus field inside the dialog has
  // taken focus — by the time any effect runs, activeElement is already that
  // field, and "give focus back" would aim at something about to unmount.
  const wasOpen = useRef(false)
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    lastFocused.current = document.activeElement
  }
  wasOpen.current = open

  useEffect(() => {
    if (!open) return undefined
    // An autoFocus field inside has already been focused; leave it there.
    if (!dialogRef.current?.contains(document.activeElement)) dialogRef.current?.focus()

    function onKeyDown(event) {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Back to whatever field asked for this — usually a search box mid-word.
      if (lastFocused.current?.focus) lastFocused.current.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="ui-modal-backdrop"
      // Only a click that started AND ended on the backdrop itself closes it —
      // a drag that starts inside a text field and releases outside one (a
      // common way to select text) must not discard what was being typed.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      onSubmit={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.stopPropagation()
      }}
    >
      <div
        // `wide` for a whole form — the product popup on a purchase asks
        // everything the Add product page does, in its two-column grid.
        className={wide ? 'ui-modal ui-modal-wide' : 'ui-modal'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="ui-modal-head">
          <h2 className="ui-modal-title">{title}</h2>
          <button
            type="button"
            className="ui-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="ui-modal-body">{children}</div>
      </div>
    </div>
  )
}

/** Skeleton rows at real column widths, never a centred spinner — so the page
 *  never reflows between loading, empty and loaded. */
export function SkeletonRows({ columns, rows = 5 }) {
  return Array.from({ length: rows }, (_, r) => (
    <tr key={r} className="ui-skel-row">
      {columns.map((c) => (
        <td key={c.key}>
          <span className="ui-skel" />
        </td>
      ))}
    </tr>
  ))
}
